// src/pages/SuperadminDashboard.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { User, Building2, UserCog } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import SuperadminApiUsageChart from '../components/dashboard/SuperadminApiUsageChart';
import SuperadminFeedbackRate from '../components/dashboard/SuperadminFeedbackRate';
import SuperadminMatchingCounts from '../components/dashboard/SuperadminMatchingCounts';
import SuperadminErrorRate from '../components/dashboard/SuperadminErrorRate';
import EmailDeliveryTile from '../components/dashboard/EmailDeliveryTile';
import LicenseUsageTile from '../components/dashboard/LicenseUsageTile';
import SuperadminChurnWarningTile from '../components/dashboard/SuperadminChurnWarningTile';
import SuperadminCriticalMatchesTile from '../components/dashboard/SuperadminCriticalMatchesTile';

import InviteHRModal from '../components/modals/InviteHRModal';

function SuperadminDashboard() {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/superadmin/overview`, {
          withCredentials: true,
        });
        setOverview(res.data);
      } catch (err) {
        setError(err.response?.data || 'Fehler beim Laden des Dashboards');
      }
    };
    fetchOverview();
  }, []);

  if (error) {
    return (
      <div className="p-6 text-red-600">
        <p>{error}</p>
        <button onClick={() => (window.location.href = '/')}>Zurück</button>
      </div>
    );
  }

  if (!overview) return <div className="p-6 text-white">Lade...</div>;

  const roleData = [
    { name: 'Mentees', value: overview.totalMentees },
    { name: 'Mentoren', value: overview.totalMentors },
    { name: 'HR', value: overview.totalHR },
  ];

  return (
    <div className="p-6 bg-black text-white min-h-screen space-y-10">
      {/* Überschrift + Invite-Button */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Superadmin-Dashboard</h1>
        <button
          onClick={() => setShowInviteModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-semibold"
        >
          HR-User einladen
        </button>
      </div>

      {/* KPI-Kacheln */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="h-full">
          <CardContent className="p-6 flex items-center gap-4">
            <Building2 size={32} />
            <div>
              <p className="text-gray-400 text-sm">Unternehmen gesamt</p>
              <p className="text-2xl font-bold">{overview.totalCustomers}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="h-full">
          <CardContent className="p-6 flex items-center gap-4">
            <User size={32} />
            <div>
              <p className="text-gray-400 text-sm">Aktive Nutzer:innen (30d)</p>
              <p className="text-2xl font-bold">
                {overview.activeUsers} / {overview.totalUsers}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="h-full">
          <CardContent className="p-6 flex items-center gap-4">
            <UserCog size={32} />
            <div>
              <p className="text-gray-400 text-sm">Onboarding abgeschlossen</p>
              <p className="text-2xl font-bold">{overview.onboardingCompletionRate}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rollenverteilung */}
      <div className="bg-gray-900 p-6 rounded-2xl shadow">
        <h2 className="text-xl font-semibold mb-4">Nutzer nach Rolle</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={roleData} layout="vertical">
            <XAxis type="number" hide />
            <YAxis dataKey="name" type="category" stroke="#ccc" />
            <Tooltip />
            <Bar dataKey="value" fill="#22c55e" radius={[0, 8, 8, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Weitere Kacheln */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <SuperadminApiUsageChart />
        <SuperadminFeedbackRate />
        <SuperadminMatchingCounts />
        <SuperadminErrorRate />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <EmailDeliveryTile />
        <LicenseUsageTile overview={overview} />
        <SuperadminChurnWarningTile />
        <SuperadminCriticalMatchesTile />
      </div>

      {/* Invite Modal */}
      <InviteHRModal
        isOpen={showInviteModal}
        onRequestClose={() => setShowInviteModal(false)}
      />
    </div>
  );
}

export default SuperadminDashboard;
